export const INVALID_VALUES = ['nan', 'null', '-', 'undefined'];
export const MAX_PERSONNEL_ROWS = 24;

export const isValidValue = (value) => {
  if (value === null || value === undefined) return false;
  const text = String(value).trim();
  return text !== '' && !INVALID_VALUES.includes(text.toLowerCase());
};

export const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();

export const formatPartnerSource = (value) => {
  const normalized = normalizeText(value);
  const hasSuiviMarche = normalized.includes('SUIVI MARCHE');
  const hasPartenaire = normalized.includes('PARTENAIRE');

  if (hasSuiviMarche && hasPartenaire) return 'Suivi marché / Partenaire';
  if (hasSuiviMarche) return 'Suivi marché';
  return 'Partenaire';
};

export const normalizeEvalResult = (value) => {
  if (!isValidValue(value)) return '';
  const upper = normalizeText(value);

  if (
    [
      'BONNE PERFORMANCE',
      'BONNE PERFORMACE',
      'PREFORMANCE SATISFAISANTE',
      'SUPERIEUR',
      'SUPERIEURE',
      'SUPERIEUR(E)',
    ].includes(upper)
  ) {
    return 'BONNE PERFORMANCE';
  }
  if (
    [
      'PERFORMANCE MOYENNE',
      'PERFORMANCE  MOYENNE',
      'PERFORMANCE OYENNE',
      'MOYENNE PERFORMANCE',
      'MOYENNE',
      'PERFORMANCE MOYENNE SOUS RESERVE',
    ].includes(upper)
  ) {
    return 'PERFORMANCE MOYENNE';
  }
  if (
    [
      'PERFORMANCE INSUFFISANTE',
      'PERFORMANCE INSUFISANT',
      'PRESTATION A AMELIORER',
      'INSUFFISANT',
    ].includes(upper)
  ) {
    return 'PERFORMANCE INSUFFISANTE';
  }
  if (
    ['ACCEPTABLE', 'SATISFAISANT', 'SATISFAISANTE', 'QUALIFIE', 'RETENU', 'AMELIORE'].includes(
      upper
    )
  ) {
    return 'ACCEPTABLE';
  }

  return String(value).trim();
};

export const slugifyPartnerName = (value) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['`-]/g, ' ')
    .replace(/[^A-Z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const createPartnerIdAssigner = () => { // permet d'attribuer un ID unique à chaque partenaire en se basant sur son nom, avec une logique de détection de similarité pour éviter les doublons
  const partnerIdRegistry = new Map();
  let sequence = 1;

  return (partnerName) => {
    const slug = slugifyPartnerName(partnerName);
    if (!slug) return '';
    if (partnerIdRegistry.has(slug)) return partnerIdRegistry.get(slug);

    let foundId = '';
    if (slug.length >= 6) {
      const prefix = slug.slice(0, 6);
      for (const [knownSlug, knownId] of partnerIdRegistry.entries()) {
        if (knownSlug.length < 6) continue;
        if (!knownSlug.startsWith(prefix)) continue;
        if (Math.abs(knownSlug.length - slug.length) > 3) continue;

        const sharedChars = Array.from(slug).filter((char, index) => knownSlug[index] === char).length;
        const similarityRatio = sharedChars / Math.max(slug.length, knownSlug.length);
        if (similarityRatio > 0.85) {
          foundId = knownId;
          break;
        }
      }
    }

    const partnerId = foundId || `P-${String(sequence++).padStart(5, '0')}`;
    partnerIdRegistry.set(slug, partnerId);
    return partnerId;
  };
};

export const getPersonnelMatchKey = (nomPersonnel, cin) => {
  const cinClean = String(cin || '')
    .replace(/\s/g, '')
    .toUpperCase();
  if (cinClean) return `CIN:${cinClean}`;
  const slug = slugifyPartnerName(nomPersonnel);
  return slug ? `NOM:${slug}` : '';
};

export const createPersonnelIdAssigner = () => {
  const personnelIdRegistry = new Map();
  let sequence = 1;

  return (nomPersonnel, cin) => {
    const key = getPersonnelMatchKey(nomPersonnel, cin);
    if (!key) return '';
    if (personnelIdRegistry.has(key)) return personnelIdRegistry.get(key);

    const personnelId = `PE-${String(sequence++).padStart(5, '0')}`;
    personnelIdRegistry.set(key, personnelId);
    return personnelId;
  };
};

export const formatDate = (value, fallback = '-') => {
  if (!isValidValue(value)) return fallback;
  const parts = String(value).split('-');
  if (parts.length !== 3) return String(value);
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export const formatAmount = (value) => Number(value || 0).toLocaleString('fr-FR');

export const normalizeFinancement = (value) => {
  if (!isValidValue(value)) return '';

  const text = String(value).trim();
  return normalizeText(text) === 'FSR' ? 'PFSR' : text;
};

export const normalizeSituation = (value) => {
  const normalized = normalizeText(value);
  if (normalized === 'RESILIE' || normalized === 'RESILIEE') return 'RESILIE';
  if (normalized === 'EN COURS') return 'EN COURS';
  if (['FIN', 'TERMINE', 'TERMINEE', 'ACHEVE', 'ACHEVEE', 'ACHEVÉ'].includes(normalized)) return 'ACHEVÉ';
  return normalized || '';
};

export const isConsultantIndividuel = (row) => {
  if (row?.consultantIndividuel === true) return true;
  const statut = normalizeText(row?.statutJuridique);
  return statut.includes('CONSULTANT');
};

export const hasDateOverlap = (startA, endA, startB, endB) => {
  if (!isValidValue(startA) || !isValidValue(endA) || !isValidValue(startB) || !isValidValue(endB)) {
    return false;
  }
  return String(startA) <= String(endB) && String(startB) <= String(endA);
};

export const getSituationDisplay = (situationsSet) => {
  const normalizedSet =
    situationsSet instanceof Set ? situationsSet : new Set(Array.isArray(situationsSet) ? situationsSet : []);

  if (normalizedSet.has('EN COURS')) return 'EN COURS';
  if (normalizedSet.has('RESILIE') || normalizedSet.has('RESILIEE')) return 'RESILIE';
  return 'ACHEVÉ';
};

export const getSituationValue = (situationsSet) => {
  if (situationsSet.has('EN COURS')) return 'EN COURS';
  if (situationsSet.has('RESILIE') || situationsSet.has('RESILIEE')) return 'RESILIE';
  return 'ACHEVÉ';
};

export const getSituationChipSx = (situation) => {
  if (situation === 'EN COURS') {
    return { bgcolor: 'rgba(52,211,153,.15)', color: '#16a34a', borderColor: 'rgba(52,211,153,.35)' };
  }
  if (situation === 'RESILIE') {
    return { bgcolor: 'rgba(248,113,113,.15)', color: '#dc2626', borderColor: 'rgba(248,113,113,.35)' };
  }
  return { bgcolor: 'rgba(75,96,128,.2)', color: '#64748b', borderColor: 'rgba(75,96,128,.35)' };
};

export const getEvaluationMeta = (value) => {
  const mapping = {
    'BONNE PERFORMANCE': {
      label: 'BONNE PERFORMANCE',
      sx: { bgcolor: 'rgba(52,211,153,.15)', color: '#16a389', borderColor: 'rgba(52,211,153,.35)' },
    },
    'PERFORMANCE MOYENNE': {
      label: 'PERFORMANCE MOYENNE',
      sx: { bgcolor: 'rgba(251,191,36,.15)', color: '#d97706', borderColor: 'rgba(251,191,36,.35)' },
    },
    'PERFORMANCE INSUFFISANTE': {
      label: 'PERFORMANCE INSUFFISANTE',
      sx: { bgcolor: 'rgba(248,113,113,.15)', color: '#dc2626', borderColor: 'rgba(248,113,113,.35)' },
    },
    ACCEPTABLE: {
      label: 'ACCEPTABLE',
      sx: { bgcolor: 'rgba(79,142,247,.16)', color: '#2563eb', borderColor: 'rgba(79,142,247,.35)' },
    },
  };

  return (
    mapping[value] || {
      label: value || 'Non renseigne',
      sx: { bgcolor: '#f5f5f5', color: '#6b7280', borderColor: '#d1d5db' },
    }
  );
};

export const MONTHS = [
  'Janvier',
  'Fevrier',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Aout',
  'Septembre',
  'Octobre',
  'Novembre',
  'Decembre',
];

export const parseNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export const normalizeDate = (value) => {
  if (!isValidValue(value)) return '';
  const text = String(value).trim();
  return text.length >= 10 ? text.slice(0, 10) : text;
};

export const formatCompactAmount = (value) => { // formate les montants en K/M/G pour une lecture plus facile
  const number = Number(value || 0);
  const abs = Math.abs(number);
  if (abs >= 1e9) return `${(number / 1e9).toFixed(1)} Milliards`;
  if (abs >= 1e6) return `${(number / 1e6).toFixed(1)} Millions`;
  if (abs >= 1e3) return `${(number / 1e3).toFixed(1)} K`;
  return number.toLocaleString('fr-FR');
};

export const isInfraMarket = (row) => { // determine si un marche est de type infra en se basant sur la composante et l'objet du marche
  const composante = normalizeText(row.composante || '');
  const objet = normalizeText(row.objet || '');
  if (composante.includes('INFRA')) return true;

  return ['REHABILITATION', 'RECONSTRUCTION', 'TRAVAUX DE', 'SUPERVISION ET CONTROLE DES TRAVAUX'].some((key) =>
    objet.includes(key)
  );
};

export const parseMaybeJson = (value, fallback = null) => {
  if (!value) {
    return fallback;
  }
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

export const normalizeValue = (value) => String(value ?? '').trim();

export const formatFieldLabel = (key) => (
  String(key || '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
);

export const escapeCsvValue = (value) => {
    const stringValue = value === null || value === undefined ? '' : String(value);
    return `"${stringValue.replace(/"/g, '""')}"`;
  };

export  const escapeHtml = (value) => String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
